import { useEffect, useState } from 'react';
import {
  Button,
  Input,
  Badge,
  Skeleton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
} from '@vnetwork/ui/primitives';
import { Section, ActionBar, SettingRow } from '@vnetwork/ui/layout';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useObjectLock,
  useSetObjectLock,
  s3ExtractError,
  type ObjectLockMode,
} from '@/api/s3';

type Unit = 'days' | 'years';

/**
 * Default object-lock retention. Object lock itself is fixed at bucket creation,
 * so this only edits the retention policy — and only when the bucket has lock
 * on. The backend needs exactly one of days/years, so the UI carries a single
 * amount + a unit toggle rather than two independent inputs.
 */
export function BucketObjectLockTab({
  serviceId,
  bucket,
}: {
  serviceId: string;
  bucket: string;
}) {
  const toast = useToast();
  const { data, isLoading } = useObjectLock(serviceId, bucket);
  const save = useSetObjectLock(serviceId, bucket);

  const [mode, setMode] = useState<ObjectLockMode>('GOVERNANCE');
  const [amount, setAmount] = useState('');
  const [unit, setUnit] = useState<Unit>('days');

  useEffect(() => {
    if (!data) return;
    if (data.mode) setMode(data.mode);
    if (data.years && data.years > 0) {
      setUnit('years');
      setAmount(String(data.years));
    } else if (data.days && data.days > 0) {
      setUnit('days');
      setAmount(String(data.days));
    }
  }, [data]);

  if (isLoading) return <Skeleton variant="rect" height={160} />;

  if (!data?.enabled) {
    return (
      <Section
        title="Object lock"
        description="Write-once-read-many retention for this bucket."
      >
        <Card>
          <CardHeader>
            <CardTitle>Default retention</CardTitle>
          </CardHeader>
          <CardBody>
            <SettingRow
              title="Object lock"
              description="Object lock can only be enabled when the bucket is created."
              control={
                <Badge tone="neutral" appearance="subtle" size="sm">
                  Disabled
                </Badge>
              }
            />
          </CardBody>
        </Card>
      </Section>
    );
  }

  const n = Number(amount);
  const amountValid = Number.isInteger(n) && n > 0;

  const onSave = () => {
    if (!amountValid) return;
    save.mutate(
      unit === 'years' ? { mode, years: n } : { mode, days: n },
      {
        onSuccess: () => toast.success('Retention updated'),
        onError: (e) => toast.error(s3ExtractError(e, 'Failed to update retention')),
      },
    );
  };

  return (
    <Section
      title="Object lock"
      description="Write-once-read-many retention for this bucket."
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Card>
          <CardHeader>
            <CardTitle>Default retention</CardTitle>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <SettingRow
              title="Object lock"
              description="New objects inherit this default retention."
              control={
                <Badge tone="success" appearance="subtle" size="sm">
                  Enabled
                </Badge>
              }
            />

            <div style={{ display: 'grid', gap: 'var(--vn-space-4)', maxWidth: 560 }}>
              <Field label="Mode" required>
                {(p) => (
                  <Select value={mode} onValueChange={(v) => setMode(v as ObjectLockMode)}>
                    <SelectTrigger {...p}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="GOVERNANCE">Governance</SelectItem>
                      <SelectItem value="COMPLIANCE">Compliance</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </Field>

              <Field
                label="Retention period"
                required
                error={amount && !amountValid ? 'Enter a whole number greater than zero.' : undefined}
              >
                {(p) => (
                  <div style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
                    <Input
                      {...p}
                      type="number"
                      min={1}
                      value={amount}
                      invalid={!!amount && !amountValid}
                      onChange={(e) => setAmount(e.target.value)}
                      style={{ maxWidth: 160 }}
                    />
                    <Select value={unit} onValueChange={(v) => setUnit(v as Unit)}>
                      <SelectTrigger style={{ maxWidth: 140 }}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="days">Days</SelectItem>
                        <SelectItem value="years">Years</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </Field>
            </div>
          </CardBody>
        </Card>

        <ActionBar
          end={
            <Button variant="primary" size="sm" disabled={save.isPending || !amountValid} onClick={onSave}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          }
        />
      </div>
    </Section>
  );
}
